// src/components/Admin/OffersAndBanners/OffersAndBanners.js
import React, { useEffect, useState } from "react";
import BannerList from "./BannerList";
import CreateBannerModal from "./CreateBannerModal";
import config from "../../../config";
import "./Banners.css";

export default function OffersAndBanners() {
  const [activeTab, setActiveTab] = useState("banners");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* =========================
     FETCH ALL BANNERS
     ========================= */
  const fetchBanners = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${config.baseUrl}/admin/banners`);
      if (!res.ok) throw new Error("Failed to fetch banners");
      const data = await res.json();
      setBanners(data || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load banners. Please try again.");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "banners") {
      fetchBanners();
    }
  }, [activeTab]);

  /* =========================
     ACTIVATE / DEACTIVATE
     ========================= */
  const toggleBannerActive = async (banner) => {
    const action = banner.active ? "disable" : "activate";

    try {
      const res = await fetch(
        `${config.baseUrl}/admin/banners/${banner.id}/${action}`,
        { method: "PATCH" }
      );

      if (!res.ok) throw new Error("Toggle failed");

      fetchBanners();
    } catch (err) {
      console.error("Toggle banner failed", err);
      alert("Failed to update banner status");
    }
  };

  /* =========================
     DELETE BANNER
     ========================= */
  const deleteBanner = async (banner) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${banner.title}"?`
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${config.baseUrl}/admin/banners/${banner.id}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Delete failed");

      fetchBanners();
    } catch (err) {
      console.error("Delete banner failed", err);
      alert("Failed to delete banner");
    }
  };

  return (
    <div className="offers-banners-page">
      {/* HEADER */}
      <div className="page-header">
        <div>
          <h2>Offers & Banners</h2>
          <p className="page-subtitle">
            Manage promotional banners and marketing offers shown in the app
          </p>
        </div>

        {activeTab === "banners" && (
          <button
            className="btn-primary"
            onClick={() => {
              setEditingBanner(null);
              setShowCreateModal(true);
            }}
          >
            + Create Banner
          </button>
        )}
      </div>

      {/* TABS */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === "banners" ? "active" : ""}`}
          onClick={() => setActiveTab("banners")}
        >
          🖼️ Banners
        </button>
        <button
          className={`tab ${activeTab === "offers" ? "active" : ""}`}
          onClick={() => setActiveTab("offers")}
        >
          🏷️ Offers
        </button>
      </div>

      {/* CONTENT */}
      <div className="tab-content">
        {activeTab === "banners" && (
          <>
            {loading && <div className="loading-state">Loading banners…</div>}
            {error && <div className="error-message">{error}</div>}

            {!loading && !error && (
              <BannerList
                banners={banners}
                onEdit={(banner) => {
                  setEditingBanner(banner);
                  setShowCreateModal(true);
                }}
                onToggleActive={toggleBannerActive}
                onDelete={deleteBanner}
              />
            )}
          </>
        )}

        {activeTab === "offers" && (
          <div className="coming-soon">
            <h3>🚧 Offers Module Coming Soon</h3>
            <ul>
              <li>Coupon codes</li>
              <li>Flat discounts</li>
              <li>Buy 1 Get 1 offers</li>
              <li>Category & seller based offers</li>
            </ul>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <CreateBannerModal
          initialData={editingBanner}
          onClose={() => {
            setShowCreateModal(false);
            setEditingBanner(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingBanner(null);
            fetchBanners();
          }}
        />
      )}
    </div>
  );
}
